import { AppShell, Box, Burger, Drawer, Group, Paper, Stack, VisuallyHidden, useMantineTheme } from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { brandColorTokens } from "../theme/brandColorTokens";
import { productSurfaceCssVars } from "../theme/productChrome";
import classes from "./ProductShell.module.css";

const SectionNavigationTarget = createContext<HTMLElement | null | undefined>(undefined);

export const useProductShellNavigation = () => useContext(SectionNavigationTarget) !== undefined;

/** A routed page can place its navigation in the existing sidebar without lifting server state. */
export function ProductShellSectionNavigation({ children }: { children: ReactNode }) {
  const target = useContext(SectionNavigationTarget);
  return target ? createPortal(children, target) : target === undefined ? children : <div hidden>{children}</div>;
}

export type ProductShellProps = {
  children: ReactNode;
  sidebar: ReactNode;
  navigationKey: string;
  navigationLabel?: string;
  openNavigationLabel: string;
  closeNavigationLabel: string;
  mobileNavigation?: "inline" | "drawer";
};

/** Shared facilitator/analyst frame. Routing, permissions and translated copy stay with the caller. */
export function ProductShell({
  children,
  sidebar,
  navigationKey,
  navigationLabel,
  openNavigationLabel,
  closeNavigationLabel,
  mobileNavigation = "inline",
}: ProductShellProps) {
  const [opened, { toggle, close }] = useDisclosure(false);
  const drawerNavigation = mobileNavigation === "drawer";
  const theme = useMantineTheme();
  const desktop = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
  const [navigationTarget, setNavigationTarget] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    close();
  }, [navigationKey, close]);

  return (
    <SectionNavigationTarget.Provider value={navigationTarget}>
      <SidebarTargetContext.Provider value={setNavigationTarget}>
        <AppShell
          withBorder={false}
          header={{ height: { base: 56, md: 0 } }}
          navbar={{ width: 260, breakpoint: "md", collapsed: { mobile: drawerNavigation || !opened } }}
          padding={0}
          classNames={{ main: classes.main }}
          styles={{
            root: { ...productSurfaceCssVars, backgroundColor: brandColorTokens.facilitatorSidebarBackground },
            header: { backgroundColor: brandColorTokens.facilitatorSidebarBackground, borderBottom: "none" },
            navbar: { backgroundColor: brandColorTokens.facilitatorSidebarBackground, borderRight: "none" },
          }}
        >
          <AppShell.Header hiddenFrom="md" px="md" withBorder={false}>
            <Group h="100%" align="center">
              <Burger
                opened={opened}
                onClick={toggle}
                aria-expanded={drawerNavigation ? opened : undefined}
                aria-label={opened ? closeNavigationLabel : openNavigationLabel}
                color={brandColorTokens.bodyText}
              />
            </Group>
          </AppShell.Header>
          <AppShell.Navbar
            p={0}
            inert={!desktop && (drawerNavigation || !opened)}
            visibleFrom={drawerNavigation ? "md" : undefined}
          >
            {!drawerNavigation || desktop ? sidebar : null}
          </AppShell.Navbar>
          {drawerNavigation && (
            <Drawer
              keepMounted
              opened={!desktop && opened}
              onClose={close}
              position="left"
              size={260}
              padding={0}
              title={<VisuallyHidden>{navigationLabel ?? openNavigationLabel}</VisuallyHidden>}
              closeButtonProps={{ "aria-label": closeNavigationLabel }}
              styles={{
                content: { ...productSurfaceCssVars, backgroundColor: brandColorTokens.facilitatorSidebarBackground },
                header: { backgroundColor: brandColorTokens.facilitatorSidebarBackground },
                body: { height: "calc(100dvh - 60px)" },
              }}
            >
              {!desktop ? sidebar : null}
            </Drawer>
          )}
          <AppShell.Main>
            <ProductMainSurface compactMobile={drawerNavigation}>{children}</ProductMainSurface>
          </AppShell.Main>
        </AppShell>
      </SidebarTargetContext.Provider>
    </SectionNavigationTarget.Provider>
  );
}

/** Reuse the real content frame for loading and language-gate stand-ins. */
export function ProductMainSurface({
  children,
  "aria-hidden": ariaHidden,
  compactMobile = false,
  withVerticalPadding = false,
}: {
  children: ReactNode;
  "aria-hidden"?: boolean;
  compactMobile?: boolean;
  withVerticalPadding?: boolean;
}) {
  return (
    <Box
      className={[classes.mainOuter, compactMobile && classes.compactMobile].filter(Boolean).join(" ")}
      flex={1}
      miw={compactMobile ? 0 : undefined}
      aria-hidden={ariaHidden}
    >
      <Paper
        radius="lg"
        className={classes.contentPaper}
        px={compactMobile ? { base: "md", sm: "xl", md: "2xl" } : { base: "xl", sm: "2xl" }}
        py={withVerticalPadding ? "2xl" : undefined}
        withBorder={false}
        bg={brandColorTokens.facilitatorMainContentPaper}
      >
        {children}
      </Paper>
    </Box>
  );
}

const SidebarTargetContext = createContext<((node: HTMLDivElement | null) => void) | undefined>(undefined);

export function ProductSidebar({
  brand,
  organization,
  children,
  footer,
  scrollable = false,
}: {
  brand: ReactNode;
  organization?: ReactNode;
  children: ReactNode;
  footer: ReactNode;
  scrollable?: boolean;
}) {
  const setTarget = useContext(SidebarTargetContext);
  return (
    <Stack
      justify="space-between"
      h="100%"
      py="lg"
      px="md"
      gap={scrollable ? "lg" : undefined}
      className={scrollable ? classes.scrollableSidebar : undefined}
      bg={brandColorTokens.facilitatorSidebarBackground}
    >
      <Stack gap="lg" className={scrollable ? classes.sidebarContent : undefined}>
        {brand}
        {organization}
        {children}
        <div ref={setTarget} className={classes.sectionNavigation} />
      </Stack>
      <Box className={scrollable ? classes.sidebarFooter : undefined}>{footer}</Box>
    </Stack>
  );
}
